
import Component, {mixins} from 'vue-class-component';
import {Prop} from 'vue-property-decorator';
import {namespace} from 'vuex-class';
import {IRoute, IRouteLeg, IRouteOrder, IRouteStop} from '@/model/Route/routeInterfaces';
import {UrlHelper} from '@/helpers/UrlHelper';
import {buildParsedRouteLegs} from '@/helpers/routeDirectionsHelper';
import {uiBus} from '@/plugins/uiBus';
import RouteExtraStopDialog from '@/components/route/RouteExtraStopDialog.vue';
import RouteStartingLocationDialog from '@/components/route/RouteStartingLocationDialog.vue';
import RoutePageHeader from '@/components/route/RoutePageHeader.vue';
import RouteAvailableOrders from '@/components/route/RouteAvailableOrders.vue';
import RouteStopsEditor from '@/components/route/RouteStopsEditor.vue';
import {RouteFormMixin} from '@/helpers/RouteFormMixin';

const routeModule = namespace('routeModule');

@Component({
  components: {RouteExtraStopDialog, RouteStartingLocationDialog, RoutePageHeader, RouteAvailableOrders, RouteStopsEditor},
})
export default class RouteEdit extends mixins(RouteFormMixin) {
  @Prop() protected routeId!: number;

  @routeModule.Getter('route')      protected route!: IRoute | null;
  @routeModule.Getter('routeOrders') protected routeOrders!: IRouteOrder[];
  @routeModule.Action('fetchRoute')   protected fetchRoute!: (routeId: number) => any;
  @routeModule.Action('updateRoute')  protected updateRouteAction!: (data: {routeId: number; payload: any}) => any;

  protected selectedVisible: boolean = true;
  private loadCount: number = 0;
  private readonly LOAD_TOTAL = 3;

  protected get ordersPool(): IRouteOrder[] {
    const allOrders = this.ordersForRouting.slice();
    this.routeOrders.forEach(function(ro) {
      if (allOrders.findIndex(function(o) { return o.id === ro.id; }) === -1) {
        allOrders.push(ro);
      }
    });
    return allOrders;
  }

  protected get oldBackofficeUrl(): string {
    return UrlHelper.mayorOldRoute(this.routeId);
  }

  created() {
    this.ons = this.$ons({
      'backend.call.OK.routeLoaded':            this.onDataLoaded,
      'backend.call.OK.driversLoaded':          this.onDataLoaded,
      'backend.call.OK.ordersForRoutingLoaded':  this.onDataLoaded,
      'backend.call.KO.routeLoaded':            this.onDataLoaded,
      'backend.call.KO.driversLoaded':          this.onDataLoaded,
      'backend.call.KO.ordersForRoutingLoaded':  this.onDataLoaded,
      'backend.call.OK.routeUpdated':           this.onRouteUpdated,
      'backend.call.KO.routeUpdated':           this.onSaveError,
    });

    this.fetchRoute(this.routeId);
    this.fetchDrivers();
    this.fetchOrdersForRouting();
    this.fetchStartingLocations();
    this.fetchChannels();
  }

  protected onDataLoaded() {
    this.loadCount++;
    if (this.loadCount >= this.LOAD_TOTAL) {
      this.loading = false;
      this.populateFromRoute();
    }
  }

  protected populateFromRoute() {
    if (!this.route) { return; }

    if (this.route.driver_id) {
      this.selectedDriverId = this.route.driver_id;
    }

    this.selectedVisible = this.route.is_visible !== false;
    this.selectedOrders  = this.routeOrders.slice();
    this.$nextTick(() => {
      this.draggableStops = this.buildStopsFromRoute();
    });
  }

  protected buildStopsFromRoute(): IRouteStop[] {
    if (!this.route || !this.route.legs || this.route.legs.length === 0) {
      return this.draggableStops;
    }

    const stops: IRouteStop[] = [];
    const assignedOrderIds: {[key: number]: boolean} = {};
    const extraStopsByBeforeLegIndex: {[key: number]: Array<{name?: string; address: string; lat?: number | null; lng?: number | null}>} = {};

    (this.route.extra_stop_positions || []).forEach((extraStop) => {
      const beforeLegIndex = extraStop.before_leg_index !== null && extraStop.before_leg_index !== undefined
        ? extraStop.before_leg_index
        : extraStop.leg_index;

      if (beforeLegIndex === null || beforeLegIndex === undefined) { return; }
      if (!extraStopsByBeforeLegIndex[beforeLegIndex]) {
        extraStopsByBeforeLegIndex[beforeLegIndex] = [];
      }
      extraStopsByBeforeLegIndex[beforeLegIndex].push({
        name:    extraStop.name,
        address: extraStop.address,
        lat:     extraStop.lat,
        lng:     extraStop.lng,
      });
    });

    const addExtraStops = (beforeLegIndex: number) => {
      (extraStopsByBeforeLegIndex[beforeLegIndex] || []).forEach((extraStop, idx) => {
        stops.push({
          isExtra:     true,
          locationKey: 'extra::' + beforeLegIndex + '::' + idx + '::init',
          location:    extraStop.name || extraStop.address,
          city:        null,
          orders:      [],
          name:        extraStop.name || extraStop.address,
          address:     extraStop.address,
          lat:         extraStop.lat || undefined,
          lng:         extraStop.lng || undefined,
        });
      });
    };

    this.route.legs.forEach((leg: IRouteLeg, index: number) => {
      addExtraStops(index);
      const legOrders = this.routeOrders.filter((order) => order.route_leg_id === leg.id);

      if (legOrders.length === 0) { return; }

      legOrders.forEach((order) => { assignedOrderIds[order.id] = true; });
      const firstOrder = legOrders[0];
      const key = (firstOrder.location || '') + '||' + (firstOrder.city || '');
      stops.push({locationKey: key, location: firstOrder.location || leg.end_address || '', city: firstOrder.city || null, orders: legOrders});
    });
    addExtraStops(this.route.legs.length);

    this.routeOrders.forEach((order) => {
      if (assignedOrderIds[order.id]) { return; }

      const key = (order.location || '') + '||' + (order.city || '');
      const existingStop = stops.find((stop) => !stop.isExtra && stop.locationKey === key);
      if (existingStop) {
        existingStop.orders.push(order);
        return;
      }

      stops.push({locationKey: key, location: order.location || '', city: order.city || null, orders: [order]});
    });

    if ((!this.route.extra_stop_positions || this.route.extra_stop_positions.length === 0) && this.route.extra_stops) {
      this.route.extra_stops.forEach((address, idx) => {
        stops.push({isExtra: true, locationKey: 'extra::' + idx + '::legacy', location: address, city: null, orders: [], name: address, address});
      });
    }

    return stops;
  }

  protected async onSave() {
    if (!this.canSave) { return; }
    this.saving = true;

    const timeSlotsPayload: {[key: string]: number | null} = {};
    this.selectedOrders.forEach((order) => {
      timeSlotsPayload[String(order.id)] = this.defaultTimeSlotId(order);
    });

    try {
      const orderIds: number[] = [];
      this.draggableStops.forEach(function(stop) {
        if (!stop.isExtra) {
          stop.orders.forEach(function(o: IRouteOrder) { orderIds.push(o.id); });
        }
      });
      const routeStops = this.buildRouteCalculationStops();
      const parsedLegs = await buildParsedRouteLegs(orderIds, routeStops, this.selectedDriverId as number, this.selectedStartingLocation);

      this.updateRouteAction({
        routeId: this.routeId,
        payload: {
          is_visible:        this.selectedVisible,
          driver_id:         this.selectedDriverId,
          order_ids:         orderIds,
          order_time_slots:  timeSlotsPayload,
          parsed_legs:       parsedLegs,
          extra_stops:       this.buildExtraStopsPayload(parsedLegs),
          starting_location: this.selectedStartingLocation ? {
            address: this.selectedStartingLocation.address,
            lat:     this.selectedStartingLocation.lat,
            lng:     this.selectedStartingLocation.lng,
          } : null,
        },
      });
    } catch (e) {
      this.saving = false;
      uiBus.emit('alert', this.routeSaveErrorMessage(e));
    }
  }

  protected onRouteUpdated(payload: any) {
    this.saving = false;
    const routeId = payload && payload.route_id ? payload.route_id : this.routeId;
    this.$router.push({name: 'route', params: {routeId: String(routeId)}});
  }
}
